import { Preferences } from '@capacitor/preferences'

import type { EntitlementResponse } from './api'

/**
 * Disk-backed copy of the latest authoritative
 * {@link EntitlementResponse}. Powers the "trial countdown / unlocked
 * state" UX after a cold launch with no network — see the rationale in
 * `resources/js/stores/entitlement.ts`.
 *
 * The on-disk format pairs the verbatim server response with a wall-
 * clock timestamp of when it was written. Hosting in
 * `@capacitor/preferences` mirrors the auth token's storage, so the
 * cache survives app restarts and OS-level low-storage purges the same
 * way the token does.
 *
 * Trust model:
 *   - All non-trial statuses (subscription / lifetime / lapsed / none)
 *     are trusted from cache indefinitely. A subscription that lapses
 *     server-side eventually reaches the cache on the next online
 *     refresh; pretending the user is locked out before then would
 *     wrongly punish a paying customer with intermittent connectivity.
 *   - A cached `trial` is trusted only while `trial.ends_at` is in the
 *     future relative to the device clock. The store's `effectiveStatus`
 *     computed rolls it to `lapsed` once that window has elapsed, which
 *     is what "force connectivity once we know the trial should have
 *     expired" means in practice: the red trial-expired bar shows, every
 *     action routes through the paywall, and the paywall can't complete
 *     without network. The patient must come online to either restore a
 *     real purchase or buy. We deliberately don't try to detect device-
 *     clock manipulation here; an attacker can already roll the clock
 *     back to keep the trial alive even when the cache is intact.
 *
 * Never expires on its own. The user's directive: the app must keep
 * working for weeks without connectivity. The only timestamp we read is
 * `trial.ends_at` (which is server-provided, not relative to cache
 * write time).
 */

const STORAGE_KEY = 'lingotalk.entitlement.cache'

export interface CachedEntitlement {
  response: EntitlementResponse
  /** Wall-clock at the moment the cache was written. Diagnostic only —
   *  freshness decisions read `response.trial.ends_at`, not this. */
  cached_at: string
}

export async function loadCachedEntitlement(): Promise<CachedEntitlement | null> {
  const { value } = await Preferences.get({ key: STORAGE_KEY })
  if (value === null || value === undefined) return null
  try {
    const parsed = JSON.parse(value) as unknown
    if (!isCachedEntitlement(parsed)) return null
    return parsed
  } catch {
    return null
  }
}

export async function saveCachedEntitlement(response: EntitlementResponse): Promise<void> {
  const payload: CachedEntitlement = {
    response,
    cached_at: new Date().toISOString(),
  }
  await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(payload) })
}

export async function clearCachedEntitlement(): Promise<void> {
  await Preferences.remove({ key: STORAGE_KEY })
}

/**
 * Shape guard — defensive against a previous version of the app having
 * written a different layout under the same key. We accept the cache
 * iff every field the store reads at hydrate time is present and well-
 * typed; anything else is treated as no-cache.
 */
function isCachedEntitlement(v: unknown): v is CachedEntitlement {
  if (v === null || typeof v !== 'object') return false
  const obj = v as Record<string, unknown>
  if (typeof obj.cached_at !== 'string') return false
  const r = obj.response
  if (r === null || typeof r !== 'object') return false
  const resp = r as Record<string, unknown>
  if (typeof resp.now !== 'string') return false
  const trial = resp.trial as Record<string, unknown> | null
  if (trial === null || typeof trial !== 'object') return false
  if (
    !(trial.started_at === null || typeof trial.started_at === 'string') ||
    !(trial.ends_at === null || typeof trial.ends_at === 'string') ||
    !(trial.days_remaining === null || typeof trial.days_remaining === 'number')
  ) return false
  const ent = resp.entitlement as Record<string, unknown> | null
  if (ent === null || typeof ent !== 'object') return false
  if (typeof ent.status !== 'string') return false
  return true
}
